import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import '../styles/vu-meter.css';
export interface RoomVuMeterProps {
  audioRef?: RefObject<HTMLAudioElement>;
  analyserRef?: RefObject<AnalyserNode | null>;
  enabled?: boolean;
  muted?: boolean;
}
export default function RoomVuMeter({audioRef, analyserRef, enabled=false, muted=false}: RoomVuMeterProps) {
  const barsRef=useRef<HTMLSpanElement>(null);
  const [running,setRunning]=useState(false),[audible,setAudible]=useState(true),[visible,setVisible]=useState(() => typeof document === 'undefined' || !document.hidden);
  useEffect(()=>{
    const media=audioRef?.current;
    if(!media || !enabled){setRunning(false);return;}
    const playing=()=>setRunning(!media.paused && !media.ended && media.readyState>=2);
    const stopped=()=>setRunning(false);
    const volume=()=>setAudible(!media.muted && media.volume>0);
    const visibility=()=>{setVisible(!document.hidden);playing();};
    const starts=['playing'] as const,stops=['pause','waiting','stalled','ended','emptied','error'] as const;
    starts.forEach(event=>media.addEventListener(event,playing));
    stops.forEach(event=>media.addEventListener(event,stopped));
    media.addEventListener('volumechange',volume);document.addEventListener('visibilitychange',visibility);
    playing();volume();setVisible(!document.hidden);
    return()=>{
      starts.forEach(event=>media.removeEventListener(event,playing));
      stops.forEach(event=>media.removeEventListener(event,stopped));
      media.removeEventListener('volumechange',volume);document.removeEventListener('visibilitychange',visibility);
    };
  },[audioRef,enabled]);
  const active=enabled && running && audible && !muted && visible;
  useEffect(()=>{
    const bars=barsRef.current?.querySelectorAll<HTMLElement>('.room-vu-bar');
    if(!bars)return;
    const reset=()=>bars.forEach(bar=>{bar.style.transform='scaleY(0.08)';});
    if(!active){reset();return;}
    let frame=0;
    const analyser=analyserRef?.current;
    const data=analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    const draw=(time:number)=>{
      if(analyser && data)analyser.getByteFrequencyData(data);
      bars.forEach((bar,index)=>{
        const value=data ? data[Math.min(data.length-1,Math.floor(index*data.length/bars.length))]/255 : .3+.6*(.5+.5*Math.sin(time/220+index*1.7));
        bar.style.transform=`scaleY(${Math.max(.08,value)})`;
      });
      frame=window.requestAnimationFrame(draw);
    };
    frame=window.requestAnimationFrame(draw);
    return()=>{window.cancelAnimationFrame(frame);reset();};
  },[active,analyserRef]);
  const label=!enabled ? 'En attente d’un extrait' : muted || !audible ? 'Son coupé' : active ? 'Extrait en cours' : 'Lecture en attente';
  return <div className={`room-vu-meter ${active?'is-active':''}`} role="img" aria-label={label}>
    <span ref={barsRef} className="room-vu-bars" aria-hidden="true">{Array.from({length:14},(_,index)=><i className="room-vu-bar" key={index} style={{transform:'scaleY(0.08)'}}/>)}</span>
    <span className="room-vu-caption" aria-hidden="true">{label}</span>
  </div>;
}
